import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};
const DRAG_THRESHOLD = 6;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const numberOfCopies = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {null | {
   *  pointerId: number,
   *  startX: number,
   *  startY: number,
   *  isDragging: boolean,
   *  baseTime?: number,
   *  duration?: number,
   *  distance?: number,
   *  animation?: Animation | null,
   *  baseOffset?: number,
   *  direction?: number
   * }}
   */
  #dragState = null;

  /**
   * @type {number}
   */
  #manualOffset = 0;

  #slowDown = debounce(() => {
    if (this.#dragState || this.#animation) return;

    const animation = this.#getWrapperAnimation();

    if (!animation) return;

    const from = typeof animation.playbackRate === 'number' ? animation.playbackRate : 1;

    if (from === 0) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 0,
      onUpdate: (value) => this.#setPlaybackRate(animation, value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    if (this.#dragState) return;
    this.#slowDown.cancel();

    const animation = this.#getWrapperAnimation();

    if (!animation) return;

    const currentRate = typeof animation.playbackRate === 'number' ? animation.playbackRate : 1;

    if (currentRate === 1) return;

    const from = this.#animation?.current ?? currentRate;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => this.#setPlaybackRate(animation, value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  #cancelPlaybackRateAnimation() {
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
  }

  /**
   * @param {Animation} animation
   * @param {number} value
   */
  #setPlaybackRate(animation, value) {
    if (typeof animation.updatePlaybackRate === 'function') {
      animation.updatePlaybackRate(value);
    } else {
      animation.playbackRate = value;
    }
  }

  /**
   * @returns {Animation | null}
   */
  #getWrapperAnimation() {
    return this.refs.wrapper.getAnimations()[0] ?? null;
  }

  /**
   * @returns {number}
   */
  #getTravelDistance() {
    const { wrapper } = this.refs;
    const { width } = wrapper.getBoundingClientRect();
    const gapValue = getComputedStyle(wrapper).columnGap;
    const gap = Number.parseFloat(gapValue) || 0;

    return width / 2 + gap / 2;
  }

  /**
   * @param {Animation} animation
   * @returns {number}
   */
  #getAnimationDuration(animation) {
    const timing = animation.effect?.getComputedTiming?.();
    if (timing && typeof timing.duration === 'number') {
      return timing.duration;
    }

    const speedValue = getComputedStyle(this).getPropertyValue('--marquee-speed');
    const speed = Number.parseFloat(speedValue);

    return Number.isFinite(speed) ? speed * 1000 : 0;
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.#dragState) return;

    this.#dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      isDragging: false,
    };
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    const drag = this.#dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;

    if (!drag.isDragging) {
      if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;
      if (Math.abs(deltaX) < Math.abs(deltaY)) return;

      const animation = this.#getWrapperAnimation();
      const distance = this.#getTravelDistance();

      if (!distance) return;

      drag.isDragging = true;
      drag.startX = event.clientX;
      drag.startY = event.clientY;
      drag.animation = animation;
      drag.distance = distance;
      drag.duration = animation ? this.#getAnimationDuration(animation) : 0;
      drag.baseTime = animation?.currentTime ?? 0;
      drag.baseOffset = this.#manualOffset;
      drag.direction = this.dataset.movementDirection === 'reverse' ? 1 : -1;

      this.setPointerCapture(event.pointerId);
      this.toggleAttribute('data-dragging', true);

      this.#cancelPlaybackRateAnimation();

      if (animation) {
        this.#setPlaybackRate(animation, 0);
      }

      return;
    }

    if (event.cancelable) event.preventDefault();

    const moveX = event.clientX - drag.startX;

    if (drag.animation) {
      if (!drag.duration || !drag.distance) return;

      let nextTime = drag.baseTime + drag.direction * (moveX / drag.distance) * drag.duration;
      nextTime = ((nextTime % drag.duration) + drag.duration) % drag.duration;

      drag.animation.currentTime = nextTime;
    } else {
      this.#manualOffset = (drag.baseOffset ?? 0) + moveX;
      this.refs.wrapper.style.transform = `translate3d(${this.#manualOffset}px, 0, 0)`;
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    const drag = this.#dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    if (drag.isDragging && this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }

    this.#dragState = null;
    this.toggleAttribute('data-dragging', false);

    if (!drag.isDragging || !drag.animation) return;

    if (this.matches(':hover')) {
      this.#slowDown();
    } else {
      this.#speedUp();
    }
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve(1), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;

          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          setTimeout(() => {
            resolve(marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth));
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const newNumberOfCopies = await this.#queryNumberOfCopies();
    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies === currentNumberOfCopies) return;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
